import React from "react"
import { ResponsiveLine } from "@nivo/line"
import { chartColorScheme } from "constants/chartColorScheme"
import { LineChartTooltip } from "./CustomTooltip/CustomTooltip.component"
import type {
  NivoLineChartSeries,
  NivoLineChartPoint,
} from "types/ReportingDashboard/timeseries.types"

/**
 * Props for LineChart component
 */
interface LineChartProps {
  /** Array of series objects for Nivo line chart */
  data: NivoLineChartSeries[]
  /** Label for the X-axis (e.g., "Waves") */
  xAxisLabel?: string
  /** Label for the Y-axis (e.g., "Count (n)") */
  yAxisLabel?: string
  /** Optional function to format Y-axis tick values */
  formatYTick?: (value: number) => string
  /** Height of the chart container */
  height?: number
}

/**
 * LineChart component for visualizing time series data.
 */
const LineChart: React.FC<LineChartProps> = ({
  data,
  xAxisLabel = "X-Axis",
  yAxisLabel = "Y-Axis",
  formatYTick,
  height = 400, // Default height
}) => {
  if (!data || data.length === 0) {
    return (
      <div
        style={{ height: `${height}px` }}
        className="flex items-center justify-center text-gray-500"
      >
        No data available for the line chart.
      </div>
    )
  }

  // Determine if all y values are integers for y-axis tick formatting
  const allYValuesAreIntegers: boolean = data.every(
    (series: NivoLineChartSeries) =>
      series.data.every((point: NivoLineChartPoint) =>
        Number.isInteger(point.y)
      )
  )

  /**
   * Default Y-axis formatter when no custom formatter is provided
   */
  const defaultYAxisFormatter = (value: number): string => {
    const num = parseFloat(String(value))
    if (isNaN(num)) return String(value)
    return allYValuesAreIntegers
      ? Math.round(num).toLocaleString() // Use localeString for integers
      : num.toFixed(num % 1 !== 0 ? 2 : 0)
  }

  /**
   * Gets the color for a series based on its index
   */
  const getSeriesColor = (series: { id: string }): string => {
    const seriesIndex = data.findIndex(
      (s: NivoLineChartSeries) => s.id === series.id
    )
    return chartColorScheme[seriesIndex % chartColorScheme.length]
  }

  /**
   * Determines tick rotation based on data length
   */
  const getTickRotation = (): number => {
    return data.length > 0 && data[0].data.length > 7 ? 45 : 0
  }

  /**
   * Determines legend offset based on data length
   */
  const getLegendOffset = (): number => {
    return data.length > 0 && data[0].data.length > 7 ? 60 : 45
  }

  return (
    <div style={{ height: `${height}px`, width: "100%" }}>
      <ResponsiveLine
        data={data}
        // Increased right margin to accommodate the legend better
        margin={{ top: 50, right: 140, bottom: 70, left: 80 }} // Increased right margin from 110 to 140
        xScale={{ type: "point" }}
        yScale={{
          type: "linear",
          min: "auto",
          max: "auto",
          stacked: false,
          reverse: false,
        }}
        yFormat={formatYTick ? (value: number) => formatYTick(value) : " >-.2f"}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: getTickRotation(),
          legend: xAxisLabel,
          legendOffset: getLegendOffset(),
          legendPosition: "middle",
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 10,
          tickRotation: 0,
          legend: yAxisLabel,
          legendOffset: -70,
          legendPosition: "middle",
          format: formatYTick || defaultYAxisFormatter,
        }}
        colors={getSeriesColor}
        enablePoints={true} // Explicitly enable points
        pointSize={8} // Size of the points
        pointColor={{ theme: "background" }} // Point fill color
        pointBorderWidth={2} // Point border thickness
        pointBorderColor={{ from: "serieColor" }} // Point border color matches line
        pointLabelYOffset={-12} // Offset for potential point labels (currently disabled)
        useMesh={true} // Improves tooltip performance
        tooltip={LineChartTooltip} // Use shared tooltip
        legends={[
          {
            anchor: "bottom-right",
            direction: "column",
            justify: false,
            translateX: 120, // Position legend relative to the right edge (needs margin.right space)
            translateY: 0,
            itemsSpacing: 1, // Vertical spacing between legend items
            itemDirection: "left-to-right",
            itemWidth: 100, // Max width for legend item text before potentially wrapping (adjust if needed)
            itemHeight: 20, // Height of each legend item row
            itemOpacity: 0.85,
            symbolSize: 12, // Size of the color symbol
            symbolShape: "circle",
            effects: [
              {
                on: "hover",
                style: {
                  itemBackground: "rgba(0, 0, 0, .03)",
                  itemOpacity: 1,
                },
              },
            ],
          },
        ]}
        curve="monotoneX" // Smoother lines
        enableGridX={false} // Disable vertical grid lines
        enableGridY={true} // Enable horizontal grid lines
        theme={{
          axis: {
            ticks: { text: { fontSize: 11, fill: "#333" } },
            legend: {
              text: { fontSize: 12, fontWeight: "bold", fill: "#333" },
            },
          },
          legends: { text: { fontSize: 11, fill: "#333" } },
          tooltip: { container: { fontSize: "12px" } },
          grid: { line: { stroke: "#e0e0e0", strokeDasharray: "1 3" } }, // Style grid lines
        }}
      />
    </div>
  )
}

export default LineChart
